import fp from 'lodash/fp';
import React, {
    FC,
    FocusEventHandler,
    MouseEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FieldRenderProps } from 'react-final-form';
import { ClickAwayListener, Fade, inputAdornmentClasses, Popper, PopperProps } from '@mui/material';
import { formatDate, parseDate, getUTCOffsetConvertedDate } from '@factorin/util';
import { forms } from '@factorin/container.macro';

import { Calendar24, Close24 } from '../../icons';
import { Calendar2, CalendarProps2 } from '../Calendar2';
import { Input, InputProps } from '../Input';
import { Box } from '../Box';
import { Typography } from '../Typography';


const usePopperOpenState = (onOpen?: () => void, onClose?: () => void) => {
    const [isOpen, setOpen] = useState(() => false);

    const change = useCallback(() => {
        if (onOpen && !isOpen)
            onOpen();
        if (onClose && isOpen)
            onClose();
        setOpen(!isOpen);
    }, [isOpen, onOpen, onClose]);

    const open = useCallback(() => {
        if (onOpen)
            onOpen();
        setOpen(true);
    }, [onOpen]);

    const close = useCallback(() => {
        setOpen(false);
        if (onClose)
            onClose();
    }, [onClose]);

    return {
        isOpen,
        change,
        open,
        close,
    };
};

export type CalendarInputProps = Omit<CalendarProps2, 'Buttons'> &
Omit<InputProps, 'value' | 'onChange' | 'ref'> & { popperProps?: PopperProps, noLabel?: boolean, needCloseSuffix?: boolean, intervalError?: boolean, isUTCConvert?: boolean };


export const CalendarInput = React.forwardRef<
HTMLInputElement,
CalendarInputProps
>(({ locale = 'ru', value, onChange, onFocus, onBlur, popperProps, noLabel = false, needCloseSuffix = false, isUTCConvert = false, ...props }, ref) => {
    const { isOpen, open, close } = usePopperOpenState();

    const [inputValue, setInputValue] = useState<string>('');

    const [isDelSuffix, setIsDelSuffix] = useState(false);

    const [formatChars, setFormatChars] = useState({
        1: '[0-3]',
        2: '[0-9]',
        3: '[0-1]',
        4: '[0-9]',
        5: '[0-9]',
    });

    useEffect(() => {
        setIsDelSuffix(Boolean(value));
    }, [value]);

    useEffect(() => {
        const currentValue = inputValue || formatDate(value, locale);

        if (currentValue) {
            const [day, month] = currentValue.split('.');

            setFormatChars(previousConfig => ({
                ...previousConfig,
                1: day[0] === '_' && Number(day[1]) > 1 ? '[0-2]' : '[0-3]',
                2: day[0] === '3' ? '[0-1]' : '[0-9]',
                3: month[0] === '_' && Number(month[1]) > 2 ? '0' : '[0-1]',
                4: month[0] === '1' ? '[0-2]' : '[0-9]',
            }));
        }
    }, [setFormatChars, inputValue, value, locale]);

    const commitInputChange = useMemo(
        () =>
            fp.throttle(200)((value: string) => {
                const parsed = parseDate(value, locale) ?? undefined;

                return onChange(isUTCConvert ? getUTCOffsetConvertedDate(parsed) : parsed);
            }),
        [onChange, locale, isUTCConvert],
    );

    const onInputChange = useCallback<(value: string) => void>(
        value => {
            setInputValue(value);
            commitInputChange(value);
        }, [commitInputChange]);

    const onInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
        e => {
            setInputValue('');
            close();

            if (onBlur)
                onBlur(e);
        },
        [onBlur, close],
    );

    const onInputFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
        e => {
            open();
            if (onFocus)
                onFocus(e);
        },
        [onFocus, open],
    );

    const onInputClick = useCallback<MouseEventHandler<HTMLInputElement>>(e => {
        e.stopPropagation();
    }, []);

    const onCalendarChange = useCallback(
        (date: Date) => {
            onChange(isUTCConvert ? getUTCOffsetConvertedDate(date) : date);
            setInputValue('');
            close();
        },
        [onChange, close, isUTCConvert],
    );

    const containerRef = useRef<HTMLDivElement>();

    return (
        <Box ref={containerRef} sx={{ p: 0 }}>
            <Input
                ref={ref}
                autoComplete='off'
                maskChar='_'
                mask='12.34.5555'
                formatChars={formatChars}
                value={inputValue || formatDate(value, locale)}
                onChange={onInputChange}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                onClick={onInputClick}
                size='medium'
                suffix={needCloseSuffix && isDelSuffix ? (
                    <Close24
                        onClick={() => onChange(null)}
                        sx={({ palette }) => ({
                            color: palette.grayscale.shade100,
                        })}
                    />
                ) : (
                    <Calendar24
                        onClick={open}
                        sx={({ palette }) => ({
                            color: palette.grayscale.shade85,
                        })}
                    />
                )}
                noLabel={noLabel}
                InputProps={{
                    sx: ({ spacing }) => ({
                        [`& .${inputAdornmentClasses.root}`]: {
                            cursor: 'pointer',
                            fontSize: spacing(6),
                        },
                    }),
                }}
                {...props}
            />

            <Popper
                onFocus={open}
                open={isOpen}
                anchorEl={containerRef.current}
                container={document.getElementById('ui-portal')}
                placement='bottom-start'
                sx={{
                    zIndex: 51,
                }}
                placeholder={null}
                modifiers={[
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            fallbackPlacements: [
                                'bottom-start',
                            ],
                        },
                    },
                ]}
                transition
                {...popperProps}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={close}>
                        <Fade {...TransitionProps}>
                            <Box>
                                <Calendar2
                                    value={value}
                                    onChange={onCalendarChange}
                                    locale={locale}
                                />
                            </Box>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </Box>
    );
});

export const CalendarInputField: FC<FieldRenderProps<Date> & CalendarInputProps> = ({
    input: { name, value, onChange, onBlur, onFocus },
    meta: { modified, error, valid },
    helperText,
    onPaste,
    intervalError,
    ...props
}) => (
    <CalendarInput
        name={name}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        helperText={
            modified && !intervalError && (error || !valid) ? (
                <Typography sx={{ color: 'infoCustom.red50' }} variant='regularCaption3'>
                    <forms.errorRenderer error={error} />
                </Typography>
            ) : helperText
        }
        error={modified && (!!error || !valid) || intervalError}
        {...props}
    />
);
